import { useMemo } from "react";
import Resolve from "lib/Resolve";
import { getCustomer, getMe } from "lib/reducers/lookupData.selectors";
import { customerHasApp } from "lib/utility/customerHasApp.util";
import { useSelector } from "store";
import { FixLater } from "types";

/**
 * Utility function which searches through an array of roles and returns true if the role has the capability
 * @param {Object} me
 * @param {string} capabilityID
 * @returns {boolean}
 */
export const checkRoleHasCapability = (me: FixLater, capabilityID: string) => {
  if (!me) {
    return false;
  }
  const role = Resolve.getRole(me.roleId);
  if (!role) {
    return false;
  }
  if (role.isAdmin) {
    return true;
  }
  if (!Array.isArray(role.capabilities)) {
    return false;
  }
  for (const capability of role.capabilities) {
    if (capability.capabilityId === capabilityID) {
      return true;
    }
  }
  return false;
};

export const useRoleHasCapability = (capabilityID: string) => {
  const me = useSelector(getMe);
  return checkRoleHasCapability(me, capabilityID);
};

export const useIsAdmin = (): boolean => {
  const me = useSelector(getMe);
  if (!me) return false;
  const role = Resolve.getRole(me.roleId);
  return Boolean(role?.isAdmin);
};

export const Restrict = ({ app, capability, alternate = null, children }: FixLater) => {
  const customer = useSelector(getCustomer);
  const me = useSelector(getMe);
  const hasCapability = useMemo(() => {
    if (!capability) {
      return true;
    }
    return checkRoleHasCapability(me, capability);
  }, [me]);
  const hasApp = useMemo(() => {
    if (!app) {
      return true;
    }
    return Boolean(customer && customerHasApp(customer.apps, app));
  }, [customer]);

  if (!customer) {
    return null;
  }

  return hasApp && hasCapability ? children : alternate;
};
